import {useMemo} from "react";
import {Line} from "@react-three/drei";
import {stateColors} from "../StateColors";
import * as THREE from "three";
import {type ShapeProps} from "../../controller/ShapeController";
import {lineWidth, useShapeEffects} from "./ShapeEffects.ts";

export function InteractiveTetrahedron({position, diameter, minZ, state, controller, touchingObject}: ShapeProps) {

    const meshRef = controller.getMeshRef();
    const color = stateColors[state];

    const {edgeColor, emissiveIntensity, metalness, roughness, opacity} =
        useShapeEffects(controller, touchingObject, color, position.z, minZ);

    // tetrahedron geometry basics
    const radius = diameter * 0.70;

    // rotate so that one point is up and one is forward
    const rotationX = Math.atan(1 / Math.sqrt(2));  // ≈ -35.26°
    const rotationY = Math.PI / 4;  // 45°
    const rotationZ = Math.PI / 2;  // 180°

    // extract edges from tetrahedron geometry
    const tetraEdges = useMemo(() => {
        const geom = new THREE.TetrahedronGeometry(radius, 0);
        const pos = geom.getAttribute('position');

        // create Euler rotation and apply as matrix
        const euler = new THREE.Euler(rotationX, rotationY, rotationZ);
        const matrix = new THREE.Matrix4().makeRotationFromEuler(euler);
        geom.applyMatrix4(matrix);

        // extract all 4 vertices (TetrahedronGeometry has 12 positions, 3 per face, but only 4 unique)
        const vertices: THREE.Vector3[] = [];
        for (let i = 0; i < pos.count; i++) {
            const v = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
            // check if this vertex already exists
            const exists = vertices.some(existing => existing.distanceTo(v) < 0.001);
            if (!exists) vertices.push(v);
        }

        // 6 edges between the 4 vertices
        const edges: [[number, number, number], [number, number, number]][] = [];
        for (let i = 0; i < vertices.length; i++) {
            for (let j = i + 1; j < vertices.length; j++) {
                edges.push([
                    [vertices[i].x, vertices[i].y, vertices[i].z],
                    [vertices[j].x, vertices[j].y, vertices[j].z]
                ]);
            }
        }

        geom.dispose();
        return edges;
    }, [radius, rotationX, rotationY, rotationZ]);

    return (<>
            <mesh ref={meshRef} position={[position.x, position.y, position.z]}
                  rotation={[rotationX, rotationY, rotationZ]}
                  {...controller.getPointerHandlers()}
            >
                <tetrahedronGeometry args={[radius,0]} />
                <meshStandardMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={emissiveIntensity}
                    transparent opacity={opacity}
                    roughness={roughness}
                    metalness={metalness}
                />
                <pointLight color={color} intensity={1.5} distance={10}/>
            </mesh>
            {tetraEdges.map((edge, i) => (
                <Line
                    key={i}
                    points={edge}
                    position={[position.x, position.y, position.z]}
                    color={edgeColor ?? color}
                    lineWidth={lineWidth}
                />
            ))}
        </>
    )
}
